<template>
  <d-modal
    size="lg"
    @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('customer.addCustomer') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <d-form>
        <validation-observer
          ref="customer-add-form"
          tag="form"
          @keydown.enter.prevent>
          <div class="form-group">
            <label>
              {{ $t('ui.nameAndSurename') }}:
            </label>

            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('ui.emailAddress') }}:
            </label>

            <validation-provider
              rules="email"
              name="email"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="email" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('customer.telephone') }}:
            </label>

            <validation-provider
              rules="required"
              name="phone"
              v-slot="{ errors }">
              <d-input
                v-mask="'+## ### ### ### ### ### ### ### ### ###'"
                :state="errors.length ? 'invalid' : null"
                v-model="phone" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <d-row>
            <d-col cols="12" md="3" lg="3">
              <div class="form-group">
                <label>
                  {{ $t('ui.language') }}:
                </label>

                <div :class="{ 'form-control': true, 'is-select': true }">
                  <v-select
                    :options="languageOptions"
                    v-model="language_id"
                    :searchable="false"
                    :clearable="false"
                    :reduce="language => language.value"
                    :placeholder="$t('settings.language.chooseLanguage')"
                    label="text">
                    <template slot="no-options">
                      {{ $t('settings.language.noLanguagesAvailable') }}&hellip;
                    </template>
                  </v-select>
                </div>
              </div>
            </d-col>

            <d-col cols="12" md="9" lg="9">
              <div class="form-group">
                <label>
                  {{ $t('settings.country.country') }}:
                </label>

                <validation-provider
                  rules="required"
                  name="country-id"
                  v-slot="{ errors }">
                  <d-form-select
                    v-model="country_id"
                    :state="errors.length ? 'invalid' : null"
                    :options="countriesOptions" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>

          <d-row>
            <d-col cols="12" md="3" lg="3">
              <div class="form-group">
                <label>
                  {{ $t('settings.country.countryCode') }}:
                </label>

                <d-form-select
                  v-model="invoice_country_prefix"
                  :options="countriesCodesOptions" />
              </div>
            </d-col>

            <d-col cols="12" md="9" lg="9">
              <div class="form-group">
                <label>
                  {{ $t('customer.vatIDNumber') }}:
                </label>

                <div class="d-flex">
                  <d-input
                    v-model="invoice_tax_number" />

                  <d-button
                    v-if="invoice_country_prefix === 'PL' && invoice_tax_number"
                    theme="success"
                    :disabled="checkingTaxNumber"
                    class="ml-2 w-50 text-nowrap"
                    @click.prevent="verifyCustomerTaxID">
                    <div
                      v-if="checkingTaxNumber"
                      class="spinner-border spinner-border-sm"
                      role="status"></div>
                    {{ $t('customer.getDataFromGUS') }}
                  </d-button>
                </div>
              </div>
            </d-col>
          </d-row>

          <div class="form-group">
            <label>
              {{ $t('customer.invoiceName') }}:
            </label>

            <validation-provider
              rules="required"
              name="invoice-name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="invoice_name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label for="name">
              {{ $t('customer.street') }}:
            </label>

            <validation-provider
              rules="required"
              name="street"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="invoice_street" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="d-flex">
            <div class="form-group w-100">
              <label>
                {{ $t('customer.building') }}:
              </label>
              <d-input v-model="invoice_building" />
            </div>

            <div class="form-group w-100 ml-2">
              <label>
                {{ $t('customer.houseNumber') }}:
              </label>

              <d-input v-model="invoice_locale" />
            </div>
          </div>

          <div class="d-flex">
            <div class="form-group w-100">
              <label>
                {{ $t('customer.zipCode') }}:
              </label>

              <validation-provider
                rules="required"
                name="postcode"
                v-slot="{ errors }">
                <d-input
                  :state="errors.length ? 'invalid' : null"
                  v-model="invoice_postcode" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group w-100 ml-2">
              <label>
                {{ $t('customer.city') }}:
              </label>

              <validation-provider
                rules="required"
                name="city"
                v-slot="{ errors }">
                <d-input
                  :state="errors.length ? 'invalid' : null"
                  v-model="invoice_city" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </div>
        </validation-observer>
      </d-form>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm">
        {{ $t('ui.saveChanges') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import CommonGetters from '../../utils/CommonGetters.js';
import FormUtils from '../../utils/FormUtils.js';
import Vue from 'vue';

export default {
  name: 'order-add-customer-popup',
  computed: {
    countriesOptions () {
      return [{ value: 0, text: this.$t('settings.country.selectCountry') }].concat(CommonGetters.getCountriesForSelect(this.countries));
    },
    countriesCodesOptions () {
      return [{ value: '', text: this.$t('settings.country.selectCountry') }].concat(CommonGetters.getCountriesCodesForSelect(this.countries));
    },
    languageOptions () {
      return this.languages.map(language => ({
        value: language.id,
        text: language.code
      }));
    },
    nonVerifiedCountryPrefixes () {
      return this.countries.filter(country => country.verify_tax_number === 0).map(country => country.code);
    }
  },
  data () {
    return {
      name: '',
      email: '',
      phone: '',
      invoice_name: '',
      invoice_street: '',
      invoice_building: '',
      invoice_locale: '',
      invoice_postcode: '',
      invoice_city: '',
      invoice_country_prefix: '',
      invoice_tax_number: '',
      country_id: 0,
      language_id: 0,
      // internals
      languages: [],
      countries: [],
      loadError: false,
      loadedCountries: false,
      loadedLanguages: false,
      checkingTaxNumber: false
    };
  },
  mounted () {
    FormUtils.loadAdditionalData(this, {
      endpoint: '/customer-panel/languages/items',
      loadedKey: 'loadedLanguages',
      outputKey: 'languages',
      errorKey: 'loadError',
      successAction: () => {
        this.languages.reverse();
      }
    });

    FormUtils.loadAdditionalData(this, {
      endpoint: '/customer-panel/countries/items',
      loadedKey: 'loadedCountries',
      outputKey: 'countries',
      errorKey: 'loadError'
    });
  },
  methods: {
    handleClose () {
      this.$bus.$emit('order-close-new-customer-popup');
    },
    validateForm () {
      FormUtils.validate(this.$refs['customer-add-form'], this.saveCustomer);
    },
    saveCustomer () {
      let formDataToSend = {
        from_order: 1,
        name: this.name,
        email: this.email,
        phone: this.phone,
        country_id: this.country_id,
        language_id: this.language_id,
        invoice_name: this.invoice_name,
        invoice_street: this.invoice_street,
        invoice_building: this.invoice_building,
        invoice_locale: this.invoice_locale,
        invoice_postcode: this.invoice_postcode,
        invoice_city: this.invoice_city,
        invoice_tax_number: this.invoice_tax_number,
        invoice_country_prefix: this.invoice_country_prefix,
        region_id: '1' // TEMPORARY
      };

      FormUtils.submit({
        $http: this.$http,
        endpoint: '/customer-panel/partner-customers/store',
        formData: formDataToSend,
        successTitle: this.$t('customer.addSuccessTitle'),
        successText: this.$t('customer.addSuccessText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('customer.addErrorText'),
        successAction: (response) => {
          if (response.id) {
            this.$bus.$emit('order-customer-added-from-popup', response.id);
          }
          this.handleClose();
        }
      });
    },
    cancel () {
      this.handleClose();
    },
    verifyCustomerTaxID () {
      if (!this.invoice_country_prefix) {
        Vue.swal({
          title: this.$t('customer.youMustSelectCountryPrefix'),
          html: this.$t('customer.youMustSelectCountryPrefixDesc'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });

        return;
      }

      this.checkingTaxNumber = true;

      this.$http.get('/customer-panel/partner-customers/verify-tax-number', {
        params: {
          id: 0,
          prefix: this.invoice_country_prefix,
          number: this.invoice_tax_number
        }
      }).then(response => {
        this.checkingTaxNumber = false;

        if (response.data.success) {
          if (response.data.verification && response.data.verification.status === 'VALID') {
            if (this.invoice_country_prefix === 'PL') {
              this.invoice_city = response.data.verification.response.city;
              this.invoice_name = response.data.verification.response.name;
              this.invoice_postcode = response.data.verification.response.postCode;

              if (this.invoice_postcode && this.invoice_country_prefix === 'PL') {
                this.invoice_postcode = this.invoice_postcode.substr(0, 2) + '-' + this.invoice_postcode.substr(2, 3);
              }

              this.invoice_street = response.data.verification.response.street;

              if (this.invoice_street && this.invoice_street.substr(0, 4) === 'ul. ') {
                this.invoice_street = this.invoice_street.replace('ul. ', '');
              }

              this.invoice_building = response.data.verification.response.streetNumber;
              this.invoice_locale = response.data.verification.response.houseNumber;
            }
          } else if (response.data.integration_disabled === 1) {
            Vue.swal({
              title: this.$t('customer.verificationIntegrationDisabledTitle'),
              html: this.$t('customer.verificationIntegrationDisabledDesc'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          } else if (response.data.verification.status === 'INVALID') {
            Vue.swal({
              title: this.$t('customer.notFoundInVIESGUSTitle'),
              html: this.$t('customer.notFoundInVIESGUSText'),
              type: 'warning',
              confirmButtonText: this.$t('ui.ok'),
              buttonsStyling: true
            });
          }
        } else {
          Vue.swal({
            title: this.$t('ui.errorOccured'),
            html: this.$t('ui.tryAgain'),
            type: 'warning',
            confirmButtonText: this.$t('ui.ok'),
            buttonsStyling: true
          });
        }
      }).catch(error => {
        console.log(error);
        Vue.swal({
          title: this.$t('ui.errorOccured'),
          html: this.$t('ui.tryAgain'),
          type: 'warning',
          confirmButtonText: this.$t('ui.ok'),
          buttonsStyling: true
        });
        this.checkingTaxNumber = false;
      });
    }
  }
}
</script>
